import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Sized, compressed copies of /logo.png (612px, 456 KB): WebP for modern
// browsers, 256-colour PNG fallback. 256px covers the header logo (≤128px)
// on 2x screens; 576px covers the big hero logo (≤288px).
// (Emails still link the original /logo.png, so keep that file.)
export const LogoPicture = ({ large = false, className = '', alt = 'Bridgeway Global English', ...imgProps }) => {
  const w = large ? 576 : 256;
  const h = large ? 602 : 268;
  return (
    <picture>
      <source srcSet={`/logo-${w}.webp`} type="image/webp" />
      <img src={`/logo-${w}.png`} width={w} height={h} alt={alt} className={className} decoding="async" {...imgProps} />
    </picture>
  );
};

// Clicking the logo always goes to the landing page (or back to the top if
// already there). Pass clickable={false} where leaving the page would be
// disruptive (live lesson).
const Logo = ({ size = 'default', showText = true, className = '', clickable = true, compactOnMobile = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goHome = () => {
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const sizeClasses = {
    small: 'w-8 h-8 sm:w-10 sm:h-10',
    default: 'w-10 h-10 sm:w-12 sm:h-12',
    large: 'w-16 h-16 sm:w-24 sm:h-24',
    xlarge: 'w-24 h-24 sm:w-32 sm:h-32'
  };

  const textSizeClasses = {
    small: 'text-sm sm:text-lg',
    default: 'text-sm sm:text-lg md:text-xl',
    large: 'text-lg sm:text-2xl md:text-3xl',
    xlarge: 'text-2xl sm:text-3xl md:text-4xl'
  };

  const spacingClasses = {
    small: 'space-x-1.5 sm:space-x-2',
    default: 'space-x-2 sm:space-x-3',
    large: 'space-x-3 sm:space-x-4',
    xlarge: 'space-x-4 sm:space-x-5'
  };

  return (
    <div
      className={`flex items-center ${spacingClasses[size]} ${clickable ? 'cursor-pointer' : ''} ${className}`}
      onClick={clickable ? goHome : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goHome(); } } : undefined}
      role={clickable ? 'link' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? 'Go to Bridgeway Global English home page' : undefined}
    >
      <LogoPicture
        className={`${sizeClasses[size]} object-contain flex-shrink-0`}
        data-testid="bridgeway-logo"
        style={{ imageRendering: 'high-quality' }}
      />
      {showText && (
        <span className={`${compactOnMobile ? 'hidden sm:inline' : ''} ${textSizeClasses[size]} font-heading font-semibold text-text-primary whitespace-nowrap`}>
          Bridgeway Global English
        </span>
      )}
    </div>
  );
};

export default Logo;
